import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Card, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import CurrencySelect from '../../../components/shared/CurrencySelect';
import useGroupByGroupId from '../../../hooks/site/useGroupByGroupId';
import useToast from '../../../hooks/useToast';
import { useDispatch } from 'react-redux';
import {
  addFSCampaign,
  setNewGamesProvider,
  setProviders
} from '../../../store/apps/freeSpinSlice';
import FSGameCards from './FSGameCards';
import { useAppSelector } from '../../../store';
import CusPageContainer from '../../../components/shared/CusPageContainer';
import CusInput from '../../../components/material-ui/CusInput';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import ErrorBoundary from '../../../components/material-ui/error_boundary/ErrorBoundary';
import { API_GROUP } from '../../../api/user';
import { dispatchCusEvent } from '../../../utils';
import { ENUM_CUS_EVENT, ENUM_FS_MODE_STATUS, TEST_MODE } from '../../../enums/enums';

const MAX_COUNT_GAME = 5;

export default function FSInfo() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { freeSpinId } = useParams();
  const { group, groupId } = useGroupByGroupId();
  const { errorToast, successToast } = useToast();
  const navigate = useNavigate();

  const selectAddGameRef = useRef({});

  const campaignsGroup = useAppSelector((state) => state.freeSpin.campaignByGroupId?.[groupId]);
  const providers = useAppSelector((state) => state.freeSpin.providers?.[groupId]) || [];
  const gamesProvider = useAppSelector((state) => state.freeSpin.gamesProvider?.[groupId]) || {};
  const gameBets = useAppSelector((state) => state.freeSpin.gameBets?.[groupId]) || {};

  const [info, setInfo] = useState({ nameFs: '', currency: group?.currency, provider: '' });
  const [gameCards, setGameCards] = useState({});
  const [openSelect, setOpenSelect] = useState(false);

  const isNew = freeSpinId === 'new';
  const title = isNew ? 'Add a freespins template' : 'Edit freespins template';
  const gameIdList = Object.values(gameCards).map((el) => el.gameId) || [];
  const fieldSize = { xs: 12, sm: 6 };

  const providersNames = providers.map((el) => el.name);
  const providerDouble = providersNames.filter((el, index) => providersNames.indexOf(el) !== index);
  const providersOptions = providers.map((el) => ({
    label: providerDouble.includes(el.name)
      ? `${el.providerName} (${el.sourceName})`
      : el.providerName,
    value: el.id
  }));

  const choseProvider = info.provider || providersOptions?.[0]?.value;
  const games = useMemo(() => {
    const games = {};
    for (let provider in gamesProvider) {
      const newGames = gamesProvider[provider].map((el) => ({ value: el.gameId, label: el?.name }));
      games[provider] = newGames;
    }

    return games;
  }, [gamesProvider]);

  const changeRemoveGame = (gameCards, gameId) => {
    const newGames = {};
    Object.values(gameCards).forEach((game) => {
      if (gameId == game.gameId) return;
      newGames[game.gameId] = game;
    });

    return newGames;
  };

  const changeAddGame = (gameIdList, provider) => {
    setGameCards((prev) => {
      const newGameCards = {};

      // add
      gameIdList.forEach((gameId) => {
        const currentPrev = prev[gameId];
        const curGame = gamesProvider[provider]?.find((opt) => opt.gameId === gameId);
        const name = curGame?.name || currentPrev?.name;
        const uuid = curGame?.uuid || currentPrev?.uuid;
        const curProvider = curGame?.provider || currentPrev?.provider || provider;
        const curStatus = curGame?.status || currentPrev?.status || ENUM_FS_MODE_STATUS.wait;

        newGameCards[gameId] = {
          ...currentPrev,
          name,
          gameId,
          uuid,
          provider: curProvider,
          status: curStatus
        };
      });

      const length = Object.values(newGameCards).length;
      if (length > MAX_COUNT_GAME) {
        errorToast(t("you can't add more than games", { count: MAX_COUNT_GAME }));
        return prev;
      }

      const prevLength = Object.keys(prev).length;
      const newLength = Object.keys(newGameCards).length;

      if (prevLength < newLength) setOpenSelect(false);

      return newGameCards;
    });
  };

  const addFsCampaign = (data) => {
    const campaignFs = { groupId, ...data };
    API_GROUP.bonus.freespin.campaign
      .add(campaignFs)
      .then((e) => {
        const campaignId = e.data.campaignId;
        campaignFs.campaignId = campaignId;
        navigate(`/freespin/${groupId}/list`);
        successToast(t('Successfully added'));
        dispatch(addFSCampaign({ groupId, data: campaignFs }));
      })
      .catch(console.error);
  };

  const editFsCampaign = (data) => {
    const campaignFs = { campaignId: +freeSpinId, ...data };
    API_GROUP.bonus.freespin.campaign
      .update(campaignFs)
      .then((e) => {
        navigate(`/freespin/${groupId}/list`);
        successToast(t('Successfully updated'));
        dispatch(addFSCampaign({ groupId, data: campaignFs }));
      })
      .catch(console.error);
  };

  const onSaveClick = () => {
    // запрос на создание и уже на основе ответа

    const gameServer = [];

    Object.values(gameCards).forEach((el) => {
      const bet = gameBets[el.gameId]?.find((bet) => bet.betId == el.bet.betId);
      if (!bet) {
        console.error(
          'нет информации о ставке доступные ставки :',
          gameBets,
          'текущая: ',
          el.bet,
          'id игры' + el.gameId
        );
        return;
      }
      const game = {
        ...el,
        bet
      };

      gameServer.push(game);
    });

    const campaignFs = { currency: info.currency, name: info.nameFs, games: gameServer };
    isNew ? addFsCampaign(campaignFs) : editFsCampaign(campaignFs);
  };

  const getProviderById = (gameId) => {
    return providers?.find((provider) => {
      const games = gamesProvider[provider];
      if (games?.some((game) => game.gameId == gameId)) return provider;
    });
  };

  useEffect(() => {
    if (isNew) return;

    if (!campaignsGroup || !Array.isArray(campaignsGroup)) {
      // запрос на получение групп
      console.log('no campaignsGroup list');
      dispatchCusEvent(ENUM_CUS_EVENT.getFreespinList, { groupId });
      return;
    }

    // получение инфы о компании  по ИД
    const currentFS = campaignsGroup.find((el) => el.campaignId == freeSpinId);
    const games = currentFS?.games;

    setInfo({
      id: currentFS.id,
      currency: currentFS.currency,
      nameFs: currentFS?.name,
      provider: currentFS.provider
    });

    const gameList = Object.values(games).flat();
    gameList.forEach((game) => {
      const provider = getProviderById(game.gameId) || 'unknown provider#' + gameList.length;
      console.log('provider', provider);
    });

    const newGames = {};
    games.forEach((game) => {
      newGames[game.gameId] = game;
    });
    setGameCards(newGames);
  }, [freeSpinId, isNew, campaignsGroup]);

  useEffect(() => {
    if (TEST_MODE) return;
    API_GROUP.bonus.freespin
      .getProviders({ groupId: +groupId, currency: info.currency })
      .then((e) => {
        const providers = e.data.list;
        if (providers) dispatch(setProviders({ groupId, providers }));
      })
      .catch(console.error);
  }, [groupId, info.currency]);

  useEffect(() => {
    if (!choseProvider) return;
    if (TEST_MODE) return;

    API_GROUP.bonus.freespin
      .getGamesProvider({ groupId: +groupId, providerId: choseProvider, currency: info.currency })
      .then((e) => {
        console.log(e);
        const games = e.data.list;
        dispatch(setNewGamesProvider({ groupId, provider: choseProvider, games }));
      })
      .catch(console.error);
  }, [choseProvider, groupId]);

  return (
    <CusPageContainer
      title="Freespins Template Manager"
      urlById={(id) => `/freespin/${id}/new`}
      backLink={`/freespin/${groupId}/list`}
      backText={'Return to the list of freespins templates'}
      group={group}
      loader={!group}>
      <Card>
        <Typography variant="h3">{t(title)}</Typography>
        <Grid container mt={3} spacing={2}>
          <Grid size={fieldSize}>
            <CusInput
              value={info.nameFs}
              label={'Template name'}
              onChange={(e) => setInfo((prev) => ({ ...prev, nameFs: e.target.value }))}
            />
          </Grid>
          <Grid size={fieldSize}>
            <CurrencySelect
              id={'freespin__info'}
              value={info.currency}
              setValue={(val) => setInfo((prev) => ({ ...prev, currency: val }))}
            />
          </Grid>
          <Grid size={fieldSize}>
            <CusLabelSelect
              id={'freespin_provider'}
              value={choseProvider}
              options={providersOptions}
              label={'Select provider'}
              onChange={(val) => {
                setInfo((prev) => ({ ...prev, provider: val }));
              }}
            />
          </Grid>
          <Grid size={fieldSize}>
            <CusLabelSelect
              ref={selectAddGameRef}
              id={'freespin_add_game'}
              value={gameIdList}
              options={games[choseProvider]}
              label={'Add a game to the template'}
              onChange={(val) => changeAddGame(val, choseProvider)}
              multi
              selectProps={{
                open: openSelect,
                onOpen: () => setOpenSelect(true),
                onClose: () => setOpenSelect(false)
              }}
            />
          </Grid>
          <ErrorBoundary>
            <FSGameCards
              gameCards={gameCards}
              groupId={groupId}
              info={info}
              setGameCards={setGameCards}
              onSaveClick={onSaveClick}
              changeRemoveGame={changeRemoveGame}
            />
          </ErrorBoundary>
        </Grid>
      </Card>
    </CusPageContainer>
  );
}
